import React, { useState, useContext } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Autocomplete,
  TextField,
  Avatar,
  Chip,
  Paper,
  ListItemAvatar,
  InputAdornment
} from '@mui/material';
import {
  Person as PersonIcon,
  Flight as FlightIcon,
  LocationOn as LocationIcon,
  Calculate as CalculateIcon
} from '@mui/icons-material';
import { SettingsContext } from '../contexts/SettingsContext';

const ReservationDemo = () => {
  const { formatCurrency } = useContext(SettingsContext);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [nombrePassagers, setNombrePassagers] = useState(1);

  // Real client data from Clients page
  const realClients = [
    {
      id: 1,
      nom: 'Benali',
      prenom: 'Ahmed',
      email: 'ahmed.benali@email.com',
      telephone: '+213 555 123 456',
      adresse: 'Alger, Algérie',
      niveauFidelite: 'Premium',
      preferencesVoyage: ['Culturel', 'Business']
    },
    {
      id: 2,
      nom: 'Khadra',
      prenom: 'Fatima',
      email: 'fatima.khadra@email.com',
      telephone: '+213 555 234 567',
      adresse: 'Oran, Algérie',
      niveauFidelite: 'Standard',
      preferencesVoyage: ['Familial', 'Culturel']
    },
    {
      id: 3,
      nom: 'Mansouri',
      prenom: 'Karim',
      email: 'karim.mansouri@email.com',
      telephone: '+213 555 345 678',
      adresse: 'Constantine, Algérie',
      niveauFidelite: 'Bronze',
      preferencesVoyage: ['Aventure']
    },
    {
      id: 4,
      nom: 'Bouzid',
      prenom: 'Amina',
      email: 'amina.bouzid@email.com',
      telephone: '+213 555 456 789',
      adresse: 'Annaba, Algérie',
      niveauFidelite: 'Premium',
      preferencesVoyage: ['Religieux', 'Culturel']
    },
    {
      id: 5,
      nom: 'Meziane',
      prenom: 'Youssef',
      email: 'youssef.meziane@email.com',
      telephone: '+213 555 567 890',
      adresse: 'Tlemcen, Algérie',
      niveauFidelite: 'Standard',
      preferencesVoyage: ['Détente', 'Familial']
    }
  ];

  // Real package data from Forfaits page
  const realPackages = [
    {
      id: 1,
      titre: 'Circuit Découverte Istanbul',
      destination: 'Istanbul, Turquie',
      prix: 85000,
      duree: '7 jours / 6 nuits',
      categorie: 'Culturel',
      inclusions: ['Vol A/R', 'Hôtel 4*', 'Transferts', 'Visites guidées'],
      maxPersonnes: 20
    },
    {
      id: 2,
      titre: 'Séjour Luxe Dubaï',
      destination: 'Dubaï, EAU',
      prix: 160000,
      duree: '5 jours / 4 nuits',
      categorie: 'Business',
      inclusions: ['Vol Business', 'Hôtel 5*', 'Transferts VIP'],
      maxPersonnes: 10
    },
    {
      id: 3,
      titre: 'Week-end Romantique Paris',
      destination: 'Paris, France',
      prix: 72500,
      duree: '3 jours / 2 nuits',
      categorie: 'Romantique',
      inclusions: ['Vol A/R', 'Hôtel boutique', 'Dîner croisière'],
      maxPersonnes: 15
    },
    {
      id: 4,
      titre: 'Pèlerinage Omra Premium',
      destination: 'La Mecque, Arabie Saoudite',
      prix: 120000,
      duree: '10 jours / 9 nuits',
      categorie: 'Religieux',
      inclusions: ['Vol A/R', 'Hôtel proche Haram', 'Guide religieux', 'Transferts'],
      maxPersonnes: 30
    },
    {
      id: 5,
      titre: 'Circuit Historique Rome',
      destination: 'Rome, Italie',
      prix: 95000,
      duree: '6 jours / 5 nuits',
      categorie: 'Culturel',
      inclusions: ['Vol A/R', 'Hôtel 3*', 'Visites musées', 'Guide'],
      maxPersonnes: 25
    },
    {
      id: 6,
      titre: 'Escapade Marrakech',
      destination: 'Marrakech, Maroc',
      prix: 67000,
      duree: '4 jours / 3 nuits',
      categorie: 'Détente',
      inclusions: ['Vol A/R', 'Riad traditionnel', 'Hammam'],
      maxPersonnes: 20
    }
  ];

  const getFidelityColor = (niveau) => {
    switch(niveau) {
      case 'Premium': return 'error';
      case 'Standard': return 'info';
      case 'Bronze': return 'warning';
      default: return 'default';
    }
  };

  const calculateTotal = () => {
    if (!selectedPackage || !nombrePassagers) return 0;
    const basePrice = selectedPackage.prix * nombrePassagers;
    const withTax = basePrice * 1.19; // 19% tax
    return withTax;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography 
        variant="h4" 
        sx={{ 
          fontWeight: 700,
          background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
          backgroundClip: 'text',
          textFillColor: 'transparent',
          mb: 3
        }}
      >
        🔗 Démonstration Interconnexion des Pages
      </Typography>

      <Grid container spacing={3}>
        {/* Client Selection Demo */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                👥 Clients de la page "Clients"
              </Typography>
              <Autocomplete
                value={selectedClient}
                onChange={(event, newValue) => setSelectedClient(newValue)}
                options={realClients}
                getOptionLabel={(option) => `${option.prenom} ${option.nom} - ${option.email}`}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Sélectionner un client" 
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 1 }}>
                        {option.prenom[0]}{option.nom[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {option.prenom} {option.nom}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {option.email} • {option.adresse}
                        </Typography>
                        <Chip 
                          label={option.niveauFidelite}
                          size="small"
                          color={getFidelityColor(option.niveauFidelite)}
                          sx={{ borderRadius: 2 }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {option.preferencesVoyage.map((pref, index) => (
                          <Chip 
                            key={index}
                            label={pref}
                            size="small"
                            variant="outlined"
                            color="secondary"
                            sx={{ borderRadius: 2, fontSize: '0.7rem' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}
                PaperComponent={({ children, ...props }) => (
                  <Paper 
                    {...props} 
                    sx={{ 
                      borderRadius: 2,
                      boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}
                  >
                    {children}
                  </Paper>
                )}
              />
              
              {selectedClient && (
                <Card sx={{ mt: 2, background: 'rgba(30, 64, 175, 0.05)', borderRadius: 2 }}>
                  <CardContent sx={{ py: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      ✅ Client sélectionné:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                        {selectedClient.prenom[0]}{selectedClient.nom[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {selectedClient.prenom} {selectedClient.nom}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {selectedClient.telephone}
                        </Typography>
                      </Box>
                      <Chip 
                        label={selectedClient.niveauFidelite}
                        size="small"
                        color={getFidelityColor(selectedClient.niveauFidelite)}
                        sx={{ borderRadius: 2, ml: 'auto' }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Package Selection Demo */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: 'secondary.main' }}>
                ✈️ Forfaits de la page "Forfaits"
              </Typography>
              <Autocomplete
                value={selectedPackage}
                onChange={(event, newValue) => setSelectedPackage(newValue)}
                options={realPackages}
                getOptionLabel={(option) => `${option.titre} - ${option.destination}`}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Sélectionner un forfait" 
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <FlightIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'secondary.main', mr: 1 }}>
                        <FlightIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {option.titre}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {formatCurrency(option.prix)}/pers
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {option.destination} • {option.duree}
                        </Typography>
                        <Chip 
                          label={option.categorie}
                          size="small"
                          color="info"
                          sx={{ borderRadius: 2 }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {option.inclusions.slice(0, 3).join(' • ')}
                        {option.inclusions.length > 3 && '...'}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />

              {selectedPackage && (
                <Card sx={{ mt: 2, background: 'rgba(124, 58, 237, 0.05)', borderRadius: 2 }}>
                  <CardContent sx={{ py: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      ✅ Forfait sélectionné:
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {selectedPackage.titre}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {selectedPackage.destination} • {selectedPackage.duree}
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                        {formatCurrency(selectedPackage.prix)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Real-time Calculator */}
        {selectedPackage && (
          <Grid item xs={12}>
            <Card sx={{ 
              background: 'rgba(34, 197, 94, 0.05)', 
              borderRadius: 2,
              border: '1px solid rgba(34, 197, 94, 0.2)'
            }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalculateIcon />
                  💰 Calcul en temps réel (données des Paramètres)
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Nombre de passagers"
                      type="number"
                      value={nombrePassagers}
                      onChange={(e) => setNombrePassagers(parseInt(e.target.value) || 1)}
                      inputProps={{ min: 1 }}
                      fullWidth
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, background: 'rgba(255,255,255,0.8)', borderRadius: 2 }}>
                      <Typography variant="body2" color="text.secondary">Prix unitaire</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {formatCurrency(selectedPackage.prix)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, background: 'rgba(255,255,255,0.8)', borderRadius: 2 }}>
                      <Typography variant="body2" color="text.secondary">Sous-total</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'info.main' }}>
                        {formatCurrency(selectedPackage.prix * nombrePassagers)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, background: 'rgba(255,255,255,0.8)', borderRadius: 2 }}>
                      <Typography variant="body2" color="text.secondary">Total TTC (19%)</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                        {formatCurrency(calculateTotal())}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Instructions */}
        <Grid item xs={12}>
          <Card sx={{ background: 'rgba(255, 193, 7, 0.1)', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: 'warning.main' }}>
                📋 Instructions:
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • <strong>Sélectionnez un client</strong> → Voir les données réelles de la page Clients (fidélité, préférences)
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • <strong>Sélectionnez un forfait</strong> → Voir les données réelles de la page Forfaits (prix, inclusions)
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • <strong>Changez le nombre</strong> → Voir le calcul automatique en temps réel
              </Typography>
              <Typography variant="body2">
                • <strong>C'est exactement ce que vous devriez voir</strong> dans la page Réservations!
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReservationDemo; 